import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { Users, Building2, BarChart3, Plus, Pencil, Trash2, RefreshCw, Search, FileText, Download, AlertTriangle, X, Activity } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const empty = {
  nombre: '', cargo: '', empresa_id: '',
  celular: '', email: '', password: '', plan: 'profesional',
};

const emptyEmpresa = { tipo: 'CDA', nombre: '' };

export default function Admin({ user }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [empresas, setEmpresas] = useState([]);
  const [empresasLoading, setEmpresasLoading] = useState(true);

  const loadEmpresas = async () => {
    setEmpresasLoading(true);
    try {
      const token = localStorage.getItem('nika-auth-token');
      const r = await fetch(`${API}/api/admin/empresas`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setEmpresas(d.empresas || []);
    } finally { setEmpresasLoading(false); }
  };

  // Guard: redirect if not admin
  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'admin') {
      toast({ title: 'Acceso denegado', description: 'Esta sección es solo para administradores.' });
      navigate('/dashboard');
    } else {
      loadEmpresas();
    }
  }, [user, navigate, toast]);

  if (!user || user.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900" data-testid="admin-page-title">Panel de Administrador</h1>
          <p className="text-gray-600 mt-1">{user.email} · vista global de todas las empresas</p>
        </div>

        <Tabs defaultValue="dashboard" className="w-full">
          <TabsList className="grid w-full max-w-3xl grid-cols-4">
            <TabsTrigger value="dashboard" data-testid="admin-tab-dashboard"><BarChart3 className="w-4 h-4 mr-2"/>Dashboard</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="w-4 h-4 mr-2"/>Usuarios</TabsTrigger>
            <TabsTrigger value="empresas" data-testid="admin-tab-empresas"><Building2 className="w-4 h-4 mr-2"/>Empresas</TabsTrigger>
            <TabsTrigger value="fuentes" data-testid="admin-tab-fuentes"><Activity className="w-4 h-4 mr-2"/>Estado de fuentes</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-6"><AdminDashboard /></TabsContent>
          <TabsContent value="users" className="mt-6"><AdminUsers empresas={empresas} reloadEmpresas={loadEmpresas}/></TabsContent>
          <TabsContent value="empresas" className="mt-6"><AdminEmpresas empresas={empresas} loading={empresasLoading} reload={loadEmpresas}/></TabsContent>
          <TabsContent value="fuentes" className="mt-6"><AdminSourcesStatus /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ───────── Dashboard ─────────
function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportEmpresa, setReportEmpresa] = useState(null); // { empresa_id, empresa_nombre }

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('nika-auth-token');
      const r = await fetch(`${API}/api/admin/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setData(d);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return <div className="text-gray-500">Cargando estadísticas...</div>;

  const t = data.totals || {};
  const byTipo = data.by_empresa_tipo || {};
  const byType = data.by_type || {};
  const byRisk = data.by_risk || {};
  const maxDay = Math.max(1, ...(data.by_day || []).map(d => d.count));

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={load} data-testid="admin-dashboard-refresh"><RefreshCw className="w-4 h-4 mr-1"/>Actualizar</Button>
      </div>
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard title="Usuarios" value={t.users} testid="kpi-users"/>
        <KpiCard title="Empresas" value={t.empresas} testid="kpi-empresas"/>
        <KpiCard title="Total validaciones" value={t.validations} testid="kpi-total-validations"/>
        <KpiCard title="Hoy" value={t.validations_today} accent testid="kpi-today"/>
      </div>

      {/* Tipo CDA/CIA/CRC */}
      <Card>
        <CardHeader><CardTitle>Validaciones por tipo de empresa</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {['CDA','CIA','CRC'].map(k => (
              <div key={k} className="border rounded-lg p-4 text-center">
                <div className="text-xs text-gray-500">{k}</div>
                <div className="text-3xl font-bold text-gray-900 mt-1" data-testid={`kpi-tipo-${k}`}>{byTipo[k] || 0}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Tipo de validación */}
        <Card>
          <CardHeader><CardTitle>Por tipo de consulta</CardTitle></CardHeader>
          <CardContent>
            {['person','company','vehicle'].map(k => {
              const total = Object.values(byType).reduce((a,b)=>a+b,0) || 1;
              const v = byType[k] || 0;
              const label = k === 'person' ? 'Persona' : k === 'company' ? 'Empresa' : 'Vehículo';
              return (
                <div key={k} className="mb-3">
                  <div className="flex justify-between text-sm mb-1"><span>{label}</span><span className="font-semibold">{v}</span></div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-600" style={{width: `${(v/total)*100}%`}}/>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Nivel de riesgo */}
        <Card>
          <CardHeader><CardTitle>Por nivel de riesgo</CardTitle></CardHeader>
          <CardContent>
            {[['low','Bajo','bg-green-500'],['medium','Medio','bg-yellow-500'],['high','Alto','bg-orange-500'],['critical','Crítico','bg-red-600']].map(([k,label,color]) => {
              const total = Object.values(byRisk).reduce((a,b)=>a+b,0) || 1;
              const v = byRisk[k] || 0;
              return (
                <div key={k} className="mb-3">
                  <div className="flex justify-between text-sm mb-1"><span>{label}</span><span className="font-semibold">{v}</span></div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full ${color}`} style={{width: `${(v/total)*100}%`}}/>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Por día (últimos 30) */}
      <Card>
        <CardHeader><CardTitle>Tendencia (últimos 30 días)</CardTitle></CardHeader>
        <CardContent>
          {(data.by_day || []).length === 0 ? (
            <p className="text-sm text-gray-500">Sin datos en los últimos 30 días.</p>
          ) : (
            <div className="flex items-end gap-1 h-40 border-l border-b border-gray-200 pl-2 pb-2">
              {(data.by_day || []).map(d => (
                <div key={d.date} className="flex-1 group relative flex flex-col items-center" title={`${d.date}: ${d.count}`}>
                  <div className="w-full bg-blue-500 rounded-t" style={{height: `${(d.count/maxDay)*100}%`, minHeight: 2}}/>
                  <span className="absolute -bottom-5 text-[10px] text-gray-400 hidden group-hover:block">{d.date.slice(5)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top empresas */}
      <Card>
        <CardHeader>
          <CardTitle>Empresas con más actividad</CardTitle>
          <p className="text-xs text-gray-500 mt-1">
            Haz clic en <b>Ver detalle</b> para consultar usuarios y consultas de una empresa,
            o en <b>Descargar informe SARLAFT</b> para generar el PDF de cumplimiento del mes en curso.
          </p>
        </CardHeader>
        <CardContent>
          {(data.by_empresa || []).length === 0 ? (
            <p className="text-sm text-gray-500">Aún no hay validaciones por empresa.</p>
          ) : (
            <table className="w-full text-sm" data-testid="admin-top-empresas">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2">Empresa</th>
                  <th>Tipo</th>
                  <th className="text-right">Validaciones</th>
                  <th className="text-right">Alertas</th>
                  <th className="text-right pr-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {data.by_empresa.slice(0, 10).map(e => (
                  <tr key={e.empresa_id} className="border-b last:border-0" data-testid={`admin-empresa-row-${e.empresa_id}`}>
                    <td className="py-2">{e.empresa_nombre}</td>
                    <td><Badge variant="outline">{e.empresa_tipo}</Badge></td>
                    <td className="text-right font-semibold">{e.count}</td>
                    <td className="text-right">{e.alerts > 0 ? <Badge className="bg-red-100 text-red-700">{e.alerts}</Badge> : <span className="text-gray-400">0</span>}</td>
                    <td className="text-right pr-2">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`admin-empresa-detail-${e.empresa_id}`}
                          onClick={() => setReportEmpresa({ empresa_id: e.empresa_id, empresa_nombre: e.empresa_nombre, empresa_tipo: e.empresa_tipo })}
                        >
                          <FileText className="w-3 h-3 mr-1" /> Ver detalle
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {reportEmpresa && (
        <EmpresaReportModal
          empresa={reportEmpresa}
          onClose={() => setReportEmpresa(null)}
        />
      )}
    </div>
  );
}

function KpiCard({ title, value, accent, testid }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-xs uppercase tracking-wide text-gray-500">{title}</div>
        <div className={`text-3xl font-bold mt-1 ${accent ? 'text-blue-600' : 'text-gray-900'}`} data-testid={testid}>{value || 0}</div>
      </CardContent>
    </Card>
  );
}

// ───────── Empresa SARLAFT Report Modal ─────────
function firstDayCurrentMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function todayStr() {
  return new Date().toISOString().slice(0, 10);
}
function esteMs() {
  return { since: firstDayCurrentMonth(), until: todayStr() };
}
function mesAnterior() {
  const d = new Date();
  const since = new Date(d.getFullYear(), d.getMonth() - 1, 1).toISOString().slice(0, 10);
  const until = new Date(d.getFullYear(), d.getMonth(), 0).toISOString().slice(0, 10);
  return { since, until };
}
function trimestreActual() {
  const d = new Date();
  const q = Math.floor(d.getMonth() / 3);
  const since = new Date(d.getFullYear(), q * 3, 1).toISOString().slice(0, 10);
  return { since, until: todayStr() };
}
function semestreActual() {
  const d = new Date();
  const half = d.getMonth() < 6 ? 0 : 6;
  const since = new Date(d.getFullYear(), half, 1).toISOString().slice(0, 10);
  return { since, until: todayStr() };
}

function EmpresaReportModal({ empresa, onClose }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [since, setSince] = useState(firstDayCurrentMonth());
  const [until, setUntil] = useState(todayStr());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const fetchReport = async (sinceOverride, untilOverride) => {
    const s = sinceOverride ?? since;
    const u = untilOverride ?? until;
    setLoading(true);
    try {
      const token = localStorage.getItem('nika-auth-token');
      const r = await fetch(
        `${API}/api/admin/empresas/${empresa.empresa_id}/report?since=${s}&until=${u}T23:59:59`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) throw new Error('Error al cargar el informe');
      const d = await r.json();
      setReport(d);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchReport(); }, []);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('nika-auth-token');
      const r = await fetch(
        `${API}/api/admin/empresas/${empresa.empresa_id}/report/pdf?since=${since}&until=${until}T23:59:59`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) throw new Error('Error al generar PDF');
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `informe_sarlaft_${(empresa.empresa_nombre || 'empresa').replace(/\s+/g, '_')}_${since}_${until}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally { setDownloading(false); }
  };

  const downloadValidationPdf = async (validationId, subject, type) => {
    try {
      const token = localStorage.getItem('nika-auth-token');
      const r = await fetch(
        `${API}/api/validations/${validationId}/pdf`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) throw new Error('Error al generar PDF');
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (subject || 'consulta').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
      a.download = `reporte-nika-${type || 'person'}-${safeName}-${validationId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Error al descargar PDF', description: e.message, variant: 'destructive' });
    }
  };

  const verReporteCompleto = async (validationId) => {
    try {
      const token = localStorage.getItem('nika-auth-token');
      const r = await fetch(
        `${API}/api/validations/${validationId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) throw new Error('No se pudo cargar el reporte');
      const data = await r.json();
      navigate('/reporte-completo', {
        state: {
          result:        data.result,
          tipo:          data.type,
          datos:         data.request_data,
          validation_id: data.id,
        },
      });
    } catch (e) {
      toast({ title: 'Error al abrir reporte', description: e.message, variant: 'destructive' });
    }
  };

  const summary = report?.summary || {};
  const users = report?.users || [];
  const alerts = report?.alerts || [];
  const incomplete = report?.incomplete || [];
  const validations = report?.validations || [];
  const activeUsers = users.filter(u => (u.consultas || 0) > 0);

  const fmt = (iso) => iso ? new Date(iso).toLocaleString('es-CO') : '—';
  const typeLbl = (t) => ({ person: 'Persona', company: 'Empresa', vehicle: 'Vehículo' }[t] || t || '—');
  const riskClass = (r) => ({
    low: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    critical: 'bg-red-100 text-red-800',
  }[r] || 'bg-gray-100 text-gray-700');

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="empresa-report-modal">
      <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold" data-testid="empresa-report-title">
              Informe SARLAFT — {empresa.empresa_nombre}
            </h2>
            <p className="text-sm text-gray-500">Tipo <Badge variant="outline" className="ml-1">{empresa.empresa_tipo}</Badge></p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="empresa-report-close">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Filters */}
        <div className="border-b px-6 py-3 bg-gray-50 flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="since" className="text-xs">Desde</Label>
            <Input id="since" type="date" value={since} onChange={e => setSince(e.target.value)} className="h-8 w-40" data-testid="report-since"/>
          </div>
          <div>
            <Label htmlFor="until" className="text-xs">Hasta</Label>
            <Input id="until" type="date" value={until} onChange={e => setUntil(e.target.value)} className="h-8 w-40" data-testid="report-until"/>
          </div>
          <div className="flex gap-1 flex-wrap">
            {[
              { label: 'Este mes',     fn: esteMs },
              { label: 'Mes anterior', fn: mesAnterior },
              { label: 'Trimestre',    fn: trimestreActual },
              { label: 'Semestre',     fn: semestreActual },
            ].map(({ label, fn }) => (
              <Button
                key={label}
                size="sm"
                variant="ghost"
                className="h-8 text-xs px-2 text-gray-600 hover:text-blue-700 hover:bg-blue-50"
                disabled={loading}
                onClick={() => {
                  const { since: s, until: u } = fn();
                  setSince(s);
                  setUntil(u);
                  fetchReport(s, u);
                }}
              >{label}</Button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={() => fetchReport()} disabled={loading} data-testid="report-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`}/>Aplicar
          </Button>
          <div className="flex-1"/>
          <Button size="sm" onClick={downloadPdf} disabled={downloading || loading || !report} data-testid="report-download-pdf" className="bg-blue-600 hover:bg-blue-700">
            <Download className="w-4 h-4 mr-1"/>{downloading ? 'Generando…' : 'Descargar PDF SARLAFT'}
          </Button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-6 py-4 flex-1">
          {loading && <div className="text-gray-500 py-10 text-center">Cargando informe…</div>}
          {!loading && report && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard title="Consultas" value={summary.total} testid="report-kpi-total"/>
                <div>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-xs uppercase tracking-wide text-gray-500">Alertas reales</div>
                      <div className="text-3xl font-bold mt-1 text-red-600" data-testid="report-kpi-alerts">{summary.real_alerts || 0}</div>
                    </CardContent>
                  </Card>
                </div>
                <div>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-xs uppercase tracking-wide text-gray-500">Consultas incompletas</div>
                      <div className="text-3xl font-bold mt-1 text-yellow-600" data-testid="report-kpi-incomplete">{summary.incomplete_checks || 0}</div>
                    </CardContent>
                  </Card>
                </div>
                <KpiCard title="Usuarios activos" value={activeUsers.length} testid="report-kpi-users"/>
              </div>

              {/* Users */}
              <Card>
                <CardHeader><CardTitle className="text-base">Usuarios que consultaron</CardTitle></CardHeader>
                <CardContent>
                  {activeUsers.length === 0 ? (
                    <p className="text-sm text-gray-500">Sin actividad en el período.</p>
                  ) : (
                    <table className="w-full text-sm" data-testid="report-users-table">
                      <thead>
                        <tr className="text-left text-gray-500 border-b">
                          <th className="py-2">Usuario</th><th>Email</th><th>Cargo</th><th className="text-right">Consultas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeUsers.map(u => (
                          <tr key={u.email} className="border-b last:border-0">
                            <td className="py-2">{u.nombre || '—'}</td>
                            <td>{u.email}</td>
                            <td>{u.cargo || '—'}</td>
                            <td className="text-right font-semibold">{u.consultas}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>

              {/* Alerts */}
              <Card className={alerts.length > 0 ? 'border-red-300' : ''}>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className={`w-4 h-4 ${alerts.length > 0 ? 'text-red-600' : 'text-gray-400'}`}/>
                    Hallazgos SARLAFT reales ({alerts.length})
                  </CardTitle>
                  <p className="text-xs text-gray-500">
                    Únicamente coincidencias confirmadas en listas restrictivas o antecedentes reportados.
                  </p>
                </CardHeader>
                <CardContent>
                  {alerts.length === 0 ? (
                    <p className="text-sm text-gray-600">Sin alertas confirmadas en el período.</p>
                  ) : (
                    <div className="space-y-3">
                      {alerts.map((a, i) => (
                        <div key={i} className="border border-red-200 rounded-lg p-3 bg-red-50" data-testid={`report-alert-${i}`}>
                          <div className="flex justify-between items-start mb-1">
                            <div className="font-semibold text-sm">{a.source || '—'}</div>
                            <Badge className="bg-red-600 text-white">ALERTA</Badge>
                          </div>
                          <div className="text-sm text-gray-700"><b>Sujeto:</b> {a.subject}</div>
                          <div className="text-sm text-gray-700"><b>Usuario:</b> {a.user_email} · <b>Fecha:</b> {fmt(a.created_at)}</div>
                          <div className="text-sm text-gray-800 mt-1">{a.message}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Incomplete */}
              <Card className={incomplete.length > 0 ? 'border-gray-200' : ''}>
                <CardHeader>
                  <CardTitle className="text-base">Consultas incompletas — validación manual sugerida ({incomplete.length})</CardTitle>
                  <p className="text-xs text-gray-500">
                    Estas fuentes no pudieron consultarse en el momento de la solicitud.
                  </p>
                </CardHeader>
                <CardContent>
                  {incomplete.length === 0 ? (
                    <p className="text-sm text-gray-600">Todas las consultas completaron su validación.</p>
                  ) : (
                    <table className="w-full text-sm" data-testid="report-incomplete-table">
                      <thead>
                        <tr className="text-left text-gray-500 border-b bg-gray-50">
                          <th className="py-2">Fecha</th><th>Sujeto</th><th>Fuente</th><th>Motivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {incomplete.slice(0, 50).map((inc, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-2 text-xs">{fmt(inc.created_at)}</td>
                            <td className="text-xs">{inc.subject}</td>
                            <td className="text-xs">{inc.source}</td>
                            <td className="text-xs"><Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-300">No disponible al momento de la consulta</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>

              {/* Nominal listing */}
              <Card>
                <CardHeader><CardTitle className="text-base">Listado nominal de consultas ({validations.length})</CardTitle></CardHeader>
                <CardContent>
                  {validations.length === 0 ? (
                    <p className="text-sm text-gray-500">Sin consultas en el período.</p>
                  ) : (
                    <div className="max-h-80 overflow-y-auto">
                      <table className="w-full text-sm" data-testid="report-validations-table">
                        <thead className="sticky top-0 bg-white shadow-sm">
                          <tr className="text-left text-gray-500 border-b">
                            <th className="py-2">Fecha</th><th>Usuario</th><th>Tipo</th><th>Sujeto</th><th>Score</th><th>Riesgo</th><th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {validations.map(v => (
                            <tr key={v.id} className="border-b last:border-0">
                              <td className="py-2 text-xs">{fmt(v.created_at)}</td>
                              <td className="text-xs">{v.user_email}</td>
                              <td className="text-xs">{typeLbl(v.type)}</td>
                              <td className="text-xs">{v.subject}</td>
                              <td className="text-xs font-semibold">{v.score ?? '—'}</td>
                              <td className="text-xs"><Badge className={riskClass(v.risk_level)}>{(v.risk_level || '—').toUpperCase()}</Badge></td>
                              <td className="text-xs text-right pr-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-gray-400 hover:text-blue-600"
                                  title="Descargar PDF de esta consulta"
                                  onClick={() => downloadValidationPdf(v.id, v.subject, v.type)}
                                >
                                  <Download className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-gray-400 hover:text-indigo-600"
                                  title="Ver reporte completo"
                                  onClick={() => verReporteCompleto(v.id)}
                                >
                                  <FileText className="w-3 h-3" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



// ───────── Usuarios ─────────
function AdminUsers({ empresas, reloadEmpresas }) {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null=create, otherwise user obj
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState('');
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('nika-auth-token');
      const r = await fetch(`${API}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setUsers(d.users || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!filter) return users;
    const q = filter.toLowerCase();
    return users.filter(u =>
      (u.email||'').toLowerCase().includes(q) ||
      (u.nombre||'').toLowerCase().includes(q) ||
      (u.empresa_nombre||'').toLowerCase().includes(q)
    );
  }, [users, filter]);

  const openCreate = () => {
    if (!empresas || empresas.length === 0) {
      toast({
        title: 'Aún no hay empresas',
        description: 'Primero crea una empresa en la pestaña "Empresas".',
      });
      return;
    }
    setEditing(null);
    setForm({ ...empty, empresa_id: empresas[0]?.id || '' });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      nombre: u.nombre || '', cargo: u.cargo || '',
      empresa_id: u.empresa_id || '',
      celular: u.celular || u.phone || '',
      email: u.email || '', password: '',
      plan: u.plan || 'profesional',
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.empresa_id) {
      toast({ title: 'Empresa requerida', description: 'Selecciona la empresa del usuario.' });
      return;
    }
    const token = localStorage.getItem('nika-auth-token');
    const url = editing ? `${API}/api/admin/users/${editing.id}` : `${API}/api/admin/users`;
    const method = editing ? 'PATCH' : 'POST';
    const body = editing
      ? Object.fromEntries(Object.entries(form).filter(([k,v]) => k !== 'password' || v))
      : form;
    const r = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const d = await r.json();
    if (!r.ok) { toast({ title: 'Error', description: d.detail || 'No se pudo guardar' }); return; }
    const empresaName = empresas.find(e => e.id === form.empresa_id)?.nombre || '';
    toast({ title: editing ? 'Usuario actualizado' : 'Usuario creado', description: `${form.nombre} · ${empresaName}` });
    setOpen(false);
    load();
    if (reloadEmpresas) reloadEmpresas();
  };

  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.email}? Esta acción no se puede deshacer.`)) return;
    const token = localStorage.getItem('nika-auth-token');
    await fetch(`${API}/api/admin/users/${u.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` }});
    toast({ title: 'Usuario eliminado' });
    load();
    if (reloadEmpresas) reloadEmpresas();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400"/>
          <Input placeholder="Buscar por nombre, correo o empresa" className="pl-9" value={filter} onChange={e=>setFilter(e.target.value)} data-testid="admin-users-search"/>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-4 h-4 mr-1"/>Actualizar</Button>
          <Button onClick={openCreate} data-testid="admin-create-user-btn"><Plus className="w-4 h-4 mr-1"/>Nuevo usuario</Button>
        </div>
      </div>

      {(!empresas || empresas.length === 0) && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6 text-amber-900 text-sm">
            Aún no hay empresas registradas. Ve a la pestaña <strong>Empresas</strong> y crea al menos una antes de añadir usuarios.
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? <div className="p-6 text-gray-500">Cargando...</div> : filtered.length === 0 ? (
            <div className="p-6 text-gray-500">Sin usuarios. Crea el primero con el botón "Nuevo usuario".</div>
          ) : (
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead className="bg-gray-50 text-gray-600 text-left">
                <tr>
                  <th className="px-4 py-3">Nombre</th>
                  <th className="px-4 py-3">Cargo</th>
                  <th className="px-4 py-3">Empresa</th>
                  <th className="px-4 py-3">Correo</th>
                  <th className="px-4 py-3">Celular</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{u.nombre || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{u.cargo || '—'}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className="mr-1">{u.empresa_tipo}</Badge>{u.empresa_nombre}</td>
                    <td className="px-4 py-3 text-gray-600">{u.email}</td>
                    <td className="px-4 py-3 text-gray-600">{u.celular || u.phone || '—'}</td>
                    <td className="px-4 py-3"><Badge>{u.plan}</Badge></td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`admin-edit-${u.id}`}><Pencil className="w-4 h-4"/></Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(u)} className="text-red-600" data-testid={`admin-delete-${u.id}`}><Trash2 className="w-4 h-4"/></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar usuario' : 'Nuevo usuario'}</DialogTitle>
            <DialogDescription>{editing ? 'Modifica los datos del usuario. Deja la contraseña vacía si no la quieres cambiar.' : 'Completa los datos. La empresa debe existir previamente; créala en la pestaña "Empresas".'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Nombre</Label><Input required value={form.nombre} onChange={e=>setForm({...form, nombre:e.target.value})} data-testid="admin-form-nombre"/></div>
            <div className="space-y-2"><Label>Cargo</Label><Input required value={form.cargo} onChange={e=>setForm({...form, cargo:e.target.value})} data-testid="admin-form-cargo"/></div>
            <div className="space-y-2 md:col-span-2">
              <Label>Empresa</Label>
              <Select value={form.empresa_id} onValueChange={v=>setForm({...form, empresa_id:v})}>
                <SelectTrigger data-testid="admin-form-empresa-select">
                  <SelectValue placeholder="Selecciona una empresa..."/>
                </SelectTrigger>
                <SelectContent>
                  {(empresas || []).map(e => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.tipo} — {e.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(!empresas || empresas.length === 0) && (
                <p className="text-xs text-amber-700">No hay empresas. Crea una desde la pestaña "Empresas".</p>
              )}
            </div>
            <div className="space-y-2"><Label>Celular</Label><Input required value={form.celular} onChange={e=>setForm({...form, celular:e.target.value})} data-testid="admin-form-celular"/></div>
            <div className="space-y-2"><Label>Correo</Label><Input type="email" required value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="admin-form-email"/></div>
            <div className="space-y-2 md:col-span-2"><Label>{editing ? 'Nueva contraseña (opcional)' : 'Contraseña asignada'}</Label><Input type="text" required={!editing} value={form.password} onChange={e=>setForm({...form, password:e.target.value})} placeholder={editing ? 'Dejar vacío para no cambiar' : 'Mínimo 6 caracteres'} data-testid="admin-form-password"/></div>
            <div className="space-y-2 md:col-span-2">
              <Label>Plan</Label>
              <Select value={form.plan} onValueChange={v=>setForm({...form, plan:v})}>
                <SelectTrigger data-testid="admin-form-plan"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="basico">Básico (100 consultas/mes)</SelectItem>
                  <SelectItem value="profesional">Profesional (500 consultas/mes)</SelectItem>
                  <SelectItem value="empresarial">Empresarial (2000 consultas/mes)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={()=>setOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={!form.empresa_id} data-testid="admin-form-submit">{editing ? 'Guardar cambios' : 'Crear usuario'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}


// ───────── Empresas ─────────
function AdminEmpresas({ empresas, loading, reload }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEmpresa);
  const [reportEmpresa, setReportEmpresa] = useState(null);

  const openCreate = () => { setEditing(null); setForm(emptyEmpresa); setOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ tipo: e.tipo, nombre: e.nombre }); setOpen(true); };

  const submit = async (ev) => {
    ev.preventDefault();
    const token = localStorage.getItem('nika-auth-token');
    const url = editing ? `${API}/api/admin/empresas/${editing.id}` : `${API}/api/admin/empresas`;
    const method = editing ? 'PATCH' : 'POST';
    const r = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(form),
    });
    const d = await r.json();
    if (!r.ok) { toast({ title: 'Error', description: d.detail || 'No se pudo guardar' }); return; }
    toast({ title: editing ? 'Empresa actualizada' : 'Empresa creada', description: `${form.tipo} · ${form.nombre}` });
    setOpen(false);
    reload();
  };

  const remove = async (e) => {
    if (e.users_count > 0) {
      const cascade = window.confirm(
        `La empresa "${e.nombre}" tiene ${e.users_count} usuario(s). ¿Eliminar también esos usuarios?\n\n` +
        `Aceptar = eliminar empresa Y sus usuarios (las validaciones se conservan por auditoría).\n` +
        `Cancelar = NO eliminar nada.`
      );
      if (!cascade) return;
      const token = localStorage.getItem('nika-auth-token');
      const r = await fetch(`${API}/api/admin/empresas/${e.id}?cascade=true`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) { toast({ title: 'Error', description: d.detail || 'No se pudo eliminar' }); return; }
      toast({ title: 'Empresa eliminada', description: `${d.deleted_users} usuario(s) eliminados.` });
    } else {
      if (!window.confirm(`¿Eliminar la empresa "${e.nombre}"?`)) return;
      const token = localStorage.getItem('nika-auth-token');
      const r = await fetch(`${API}/api/admin/empresas/${e.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) { toast({ title: 'Error', description: d.detail || 'No se pudo eliminar' }); return; }
      toast({ title: 'Empresa eliminada' });
    }
    reload();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          {empresas.length} empresa{empresas.length === 1 ? '' : 's'} registrada{empresas.length === 1 ? '' : 's'}.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={reload}><RefreshCw className="w-4 h-4 mr-1"/>Actualizar</Button>
          <Button onClick={openCreate} data-testid="admin-create-empresa-btn"><Plus className="w-4 h-4 mr-1"/>Nueva empresa</Button>
        </div>
      </div>

      {loading ? (
        <div className="text-gray-500">Cargando empresas...</div>
      ) : empresas.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center text-gray-600">
            <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-3"/>
            <p className="font-medium">Aún no hay empresas registradas</p>
            <p className="text-sm text-gray-500 mt-1">Crea la primera empresa para empezar a asignar usuarios.</p>
            <Button onClick={openCreate} className="mt-4">
              <Plus className="w-4 h-4 mr-1"/>Crear primera empresa
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-empresas-grid">
          {empresas.map(e => (
            <Card
                key={e.id}
                className="cursor-pointer"
                title="Doble clic para ver el detalle completo"
                onDoubleClick={() => setReportEmpresa({ empresa_id: e.id, empresa_nombre: e.nombre, empresa_tipo: e.tipo })}
              >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <Badge variant="outline">{e.tipo}</Badge>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(e)} data-testid={`admin-empresa-edit-${e.id}`}><Pencil className="w-4 h-4"/></Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(e)} className="text-red-600" data-testid={`admin-empresa-delete-${e.id}`}><Trash2 className="w-4 h-4"/></Button>
                  </div>
                </div>
                <h3 className="text-lg font-semibold mt-2 text-gray-900" data-testid={`admin-empresa-name-${e.id}`}>{e.nombre}</h3>
                <p className="text-xs text-gray-400">creada {(e.created_at||'').slice(0,10)}</p>
                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                  <div>
                    <div className="text-xl font-bold text-gray-900">{e.users_count}</div>
                    <div className="text-xs text-gray-500">usuarios</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-blue-600">{e.validations_total}</div>
                    <div className="text-xs text-gray-500">consultas</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-green-600">{e.validations_completed}</div>
                    <div className="text-xs text-gray-500">completadas</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar empresa' : 'Nueva empresa'}</DialogTitle>
            <DialogDescription>{editing ? 'Los cambios afectan a todos los usuarios y consultas históricas asociadas.' : 'Define el tipo y el nombre. No puede haber dos empresas con la misma combinación.'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo de empresa</Label>
              <Select value={form.tipo} onValueChange={v => setForm({...form, tipo: v})}>
                <SelectTrigger data-testid="admin-empresa-form-tipo"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CDA">CDA — Centro de Diagnóstico Automotor</SelectItem>
                  <SelectItem value="CIA">CIA — Centro de Inspección Automotor</SelectItem>
                  <SelectItem value="CRC">CRC — Centro de Reconocimiento de Conductores</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Nombre de la empresa</Label>
              <Input
                required
                value={form.nombre}
                onChange={e => setForm({...form, nombre: e.target.value})}
                placeholder="Ej: CDA Bogotá Norte"
                data-testid="admin-empresa-form-nombre"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="admin-empresa-form-submit">{editing ? 'Guardar cambios' : 'Crear empresa'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {reportEmpresa && (
        <EmpresaReportModal empresa={reportEmpresa} onClose={() => setReportEmpresa(null)} />
      )}
    </div>
  );
}

const SOURCE_STATUS_STYLE = {
  OK: 'bg-green-100 text-green-800 border-green-200',
  lento: 'bg-amber-100 text-amber-800 border-amber-200',
  caído: 'bg-red-100 text-red-800 border-red-200',
};

function _anthropicRow(anth) {
  const a = anth || {};
  if (!a.configured) {
    return { name: 'Anthropic', status: 'caído', detail: a.error_message || 'Clave no configurada' };
  }
  if (a.http_status >= 200 && a.http_status < 300) {
    return { name: 'Anthropic', status: 'OK', detail: `HTTP ${a.http_status}` };
  }
  const bits = [
    a.error_type,
    a.error_message,
    a.http_status != null ? `HTTP ${a.http_status}` : null,
  ].filter(Boolean);
  return { name: 'Anthropic', status: 'caído', detail: bits.join(' · ') || 'No disponible' };
}

function _capsolverRow(cap) {
  const c = cap || {};
  if (!c.configured) {
    return { name: 'CapSolver', status: 'caído', detail: 'Clave no configurada' };
  }
  if (typeof c.balance === 'number') {
    return { name: 'CapSolver', status: 'OK', detail: `Saldo: ${c.balance}` };
  }
  return { name: 'CapSolver', status: 'caído', detail: 'Sin saldo o no disponible' };
}

function _rowsFromSourcesStatus(d) {
  const rows = [
    _anthropicRow(d.anthropic),
    _capsolverRow(d.capsolver),
    {
      name: 'Mongo',
      status: d.mongo && d.mongo.ok ? 'OK' : 'caído',
      detail: d.mongo && d.mongo.ok ? 'Conectado' : 'Sin conexión',
    },
  ];
  for (const l of d.listas_restrictivas || []) {
    const upd = l.last_update ? ` · última actualización ${l.last_update}` : '';
    if (l.count == null) {
      rows.push({ name: `Lista ${l.id}`, status: 'caído', detail: `Sin datos${upd}` });
    } else {
      rows.push({ name: `Lista ${l.id}`, status: 'OK', detail: `${l.count} registros${upd}` });
    }
  }
  for (const p of d.portales || []) {
    const extra = [
      p.http_status != null ? `HTTP ${p.http_status}` : null,
      p.latency_ms != null ? `${p.latency_ms} ms` : null,
    ].filter(Boolean).join(' · ');
    rows.push({
      name: p.label || p.id,
      status: p.status || 'caído',
      detail: extra,
    });
  }
  return rows;
}

function AdminSourcesStatus() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const verify = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('nika-auth-token');
      const r = await fetch(`${API}/api/admin/sources-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg = typeof d.detail === 'string' ? d.detail : `No se pudo verificar (HTTP ${r.status})`;
        setError(msg);
        setData(null);
        return;
      }
      setData(d);
    } catch (e) {
      setError('No se pudo contactar el servidor. Intenta de nuevo.');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const rows = data ? _rowsFromSourcesStatus(data) : [];

  return (
    <Card data-testid="admin-sources-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Estado de fuentes</CardTitle>
        <Button
          onClick={verify}
          disabled={loading}
          data-testid="admin-sources-verify"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Verificar ahora
        </Button>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="py-10 text-center" data-testid="admin-sources-spinner">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
            <p className="text-sm text-gray-500">Consultando fuentes… esto puede tardar unos segundos.</p>
          </div>
        )}
        {!loading && error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-start gap-2" data-testid="admin-sources-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {!loading && !error && !data && (
          <p className="text-sm text-gray-500">Pulsa Verificar ahora para consultar el estado de las fuentes.</p>
        )}
        {!loading && data && (
          <div className="space-y-2">
            {rows.map((row) => (
              <div
                key={row.name}
                className="flex items-start justify-between gap-3 p-3 rounded-lg border bg-white"
                data-testid={`admin-source-row-${row.name}`}
              >
                <div className="min-w-0">
                  <div className="font-medium text-gray-900">{row.name}</div>
                  {row.detail ? (
                    <div className="text-xs text-gray-600 mt-0.5 break-all">{row.detail}</div>
                  ) : null}
                </div>
                <Badge className={`flex-shrink-0 ${SOURCE_STATUS_STYLE[row.status] || 'bg-gray-100 text-gray-700'}`}>
                  {row.status}
                </Badge>
              </div>
            ))}
            {data.elapsed_ms != null && (
              <p className="text-xs text-gray-400 text-right pt-1">Consulta en {data.elapsed_ms} ms</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
